import { addPerson, UNANSWERED_SCREENING, UNKNOWN_INCOME, type FilingStatus, type PersonIncome, type Screening, type YearFile } from "@ftr/tax-core";
import { Card } from "../components/ui.tsx";
import { newId } from "../lib/yearStore.ts";
import { useOrganizer } from "./context.tsx";
import { AmountInput, Chips, TextInput, YesNoField } from "./fields.tsx";

const STATUSES: { value: FilingStatus; label: string }[] = [
  { value: "single", label: "Single" },
  { value: "mfj", label: "Married filing jointly" },
  { value: "mfs", label: "Married filing separately" },
  { value: "hoh", label: "Head of household" },
  { value: "qss", label: "Qualifying surviving spouse" },
];

const QUESTIONS: { key: keyof Screening; q: string; hint?: string }[] = [
  { key: "nonresident", q: "Nonresident or dual-status taxpayer for any part of 2026?" },
  { key: "entity", q: "Trading or getting paid through an LLC taxed as a partnership, an S corp or a C corp?", hint: "A single-member LLC that hasn't elected to be taxed differently counts as no." },
  { key: "section475", q: "A Section 475(f) mark-to-market election in effect?" },
  { key: "straddlesOrHedges", q: "Any straddles or hedging transactions (offsetting positions held to reduce risk)?" },
  { key: "nonUsd", q: "Any account, payout or fee in a currency other than US dollars?", hint: "Payouts in USDC or other crypto count as yes." },
  { key: "ownAccountTraderDeductions", q: "Claiming trader tax status business deductions for trading in a personal account?" },
];

export function YouStep() {
  const { yf, update } = useOrganizer();
  const status = yf.household.filingStatus;
  const spouse = yf.people.find((p) => p.role === "spouse");
  const needsSpouse = status === "mfj" || status === "mfs";

  const setStatus = (s: FilingStatus) =>
    update((y) => {
      let next: YearFile = { ...y, household: { ...y.household, filingStatus: s } };
      if ((s === "mfj" || s === "mfs") && !next.people.some((p) => p.role === "spouse")) next = addPerson(next, { id: newId(), label: "Spouse", role: "spouse" });
      return next;
    });

  const removeSpouse = () =>
    update((y) => {
      const sp = y.people.find((p) => p.role === "spouse");
      if (!sp) return y;
      const owned = y.accounts.some((a) => a.ownerId === sp.id && a.deletedAt === null) || y.events.some((e) => e.personId === sp.id && e.deletedAt === null);
      if (owned) {
        alert("Your spouse still has accounts or records. Move or remove those first.");
        return y;
      }
      const { [sp.id]: _s, ...screening } = y.screening;
      const { [sp.id]: _i, ...income } = y.income;
      return { ...y, people: y.people.filter((p) => p.id !== sp.id), screening, income };
    });

  return (
    <div className="steps-body">
      <Card title="Your household">
        <div className="form-section">
          <Chips legend="Filing status for 2026" value={status} options={STATUSES} onChange={setStatus} />
          {yf.people.map((p) => (
            <TextInput
              key={p.id}
              label={p.role === "primary" ? "Your name or nickname" : "Spouse's name or nickname"}
              value={p.label}
              hint={p.role === "primary" ? "Only used to label your report. It stays on this device." : undefined}
              onChange={(label) => update((y) => ({ ...y, people: y.people.map((x) => (x.id === p.id ? { ...x, label } : x)) }))}
            />
          ))}
          {spouse && !needsSpouse && (
            <p className="field-hint">
              A spouse is listed but the filing status isn't married.{" "}
              <button type="button" className="link-btn" onClick={removeSpouse}>
                Remove spouse
              </button>
            </p>
          )}
        </div>
      </Card>

      {yf.people.map((p) => (
        <PersonCard key={p.id} personId={p.id} />
      ))}
    </div>
  );
}

function PersonCard({ personId }: { personId: string }) {
  const { yf, update } = useOrganizer();
  const p = yf.people.find((x) => x.id === personId)!;
  const s = yf.screening[personId] ?? UNANSWERED_SCREENING;
  const inc: PersonIncome = yf.income[personId] ?? UNKNOWN_INCOME;
  const who = p.role === "primary" ? "you" : p.label;
  const setS = (key: keyof Screening, v: "yes" | "no") => update((y) => ({ ...y, screening: { ...y.screening, [personId]: { ...s, [key]: v } } }));
  const setI = (key: keyof PersonIncome, v: PersonIncome[keyof PersonIncome]) => update((y) => ({ ...y, income: { ...y.income, [personId]: { ...inc, [key]: v } } }));

  return (
    <Card title={p.role === "primary" ? "About you" : `About ${p.label}`}>
      <div className="form-section">
        <p className="form-section-label">A few situations this report doesn't cover yet</p>
        {QUESTIONS.map((q) => (
          <YesNoField
            key={q.key}
            legend={q.q}
            hint={q.hint}
            value={s[q.key]}
            onChange={(v) => setS(q.key, v)}
          />
        ))}
      </div>
      <div className="form-section">
        <p className="form-section-label">For self-employment tax</p>
        <AmountInput
          key={`ss-${personId}`}
          label={`Social Security wages for ${who}`}
          hint="W-2 box 3 plus box 7, added up across every W-2 for this person only. Box 1 isn't the same number."
          value={inc.socialSecurityWages}
          emptyMeans="unknown"
          none={{ label: "No W-2 jobs in 2026" }}
          onChange={(v) => setI("socialSecurityWages", v)}
        />
        <AmountInput
          key={`se-${personId}`}
          label={`Other self-employment profit for ${who}`}
          hint="Net profit from any other business or 1099 work, not prop firms. A loss is entered with a minus sign."
          value={inc.otherSeEarnings}
          emptyMeans="unknown"
          allowNegative
          none={{ label: "None" }}
          onChange={(v) => setI("otherSeEarnings", v)}
        />
      </div>
    </Card>
  );
}
